<script setup lang="ts">
  import { reactive, computed, ref } from "vue";
  import { Modal, Text, Button, RadioButton } from "@ui-kit";
  import { useI18n } from "vue-i18n";
  import { useSevenDays, useMoneymapFilterLastSelected } from "./utils";
  import { default as MoneymapFilterRangeItem } from "./MoneymapFilterRangeItem.vue";
  import { default as MoneymapFilterRangeTodayModal } from "./MoneymapFilterRangeTodayModal.vue";
  import { default as MoneymapFilterRangeWeakModal } from "./MoneymapFilterRangeWeakModal.vue";
  import { default as MoneymapFilterRangeMonthModal } from "./MoneymapFilterRangeMonthModal.vue";

  const { selected_range_id, selected_display_graph_id } = defineProps<{
    selected_range_id: string;
    selected_display_graph_id: string;
  }>();
  const emit = defineEmits<{
    (e: "close"): void;
    (e: "apply", payload: { range_id: string; display_graph_id: string }): void;
  }>();

  const seven_days = useSevenDays();
  const filter = useMoneymapFilterLastSelected();
  const i18n = useI18n();

  const range_id = ref(selected_range_id);
  const display_graph_id = ref(selected_display_graph_id);

  const modalVisible = reactive({
    today: false,
    week: false,
    month: false,
  });

  const display_graph_items = computed(() => [
    { id: "all", label: i18n.t("Все") },
    { id: "amount", label: i18n.t("Только в деньгах") },
    { id: "quantity", label: i18n.t("Только в штуках") },
  ]);

  const handleClickItem = (id: string) => {
    display_graph_id.value = id;
  };

  const handleSelectToday = () => {
    range_id.value = filter.state.last_selected_id_today;
  };
  const handleSelectSevenDays = () => {
    range_id.value = seven_days.value.id;
  };
  const handleSelectWeek = () => {
    range_id.value = filter.state.last_selected_id_week;
  };
  const handleSelectMonth = () => {
    range_id.value = filter.state.last_selected_id_month;
  };

  const handleTodayChange = (id: string) => {
    range_id.value = id;
    filter.onTodayChange(id);
    modalVisible.today = false;
  };
  const handleWeekChange = (id: string) => {
    range_id.value = id;
    filter.onWeekChange(id);
    modalVisible.week = false;
  };
  const handleMonthChange = (id: string) => {
    range_id.value = id;
    filter.onMonthChange(id);
    modalVisible.month = false;
  };

  const handleClickApply = () => {
    const payload = { range_id: range_id.value, display_graph_id: display_graph_id.value };
    filter.syncLastFromAppliedRange(payload.range_id);
    emit("apply", payload);
    emit("close");
  };

  const handleClickReset = () => {
    filter.state.last_selected_id_today = "today_0";
    filter.state.last_selected_id_week = "week_0";
    filter.state.last_selected_id_month = "month_0";

    emit("apply", { range_id: "today_0", display_graph_id: "all" });
    emit("close");
  };
</script>

<template>
  <Modal close :border="false" data-testid="moneymap-filter-modal" @onClose="emit('close')">
    <template #header>
      <div class="moneymap__filter-header">
        <Text font="body-bold-18">{{ $t("Фильтры") }}</Text>
      </div>
    </template>
    <div class="moneymap__filter__range">
      <Text font="body-medium-14">{{ $t("Период отображения") }}</Text>
      <div class="moneymap__filter__list">
        <MoneymapFilterRangeItem
          :is_selected="range_id.startsWith('today')"
          :title="filter.last_today.value.subtitle"
          :subtitle="filter.last_today.value.title"
          data-testid="moneymap-filter-range-today"
          @click:item="handleSelectToday()"
          @click:btn="modalVisible.today = true"
        />
        <MoneymapFilterRangeItem
          :is_button_visible="false"
          :is_selected="range_id.startsWith('7days')"
          :title="seven_days.title"
          :subtitle="seven_days.subtitle"
          data-testid="moneymap-filter-range-7days"
          @click:item="handleSelectSevenDays()"
        />
        <MoneymapFilterRangeItem
          :is_selected="range_id.startsWith('week')"
          :title="$t('Неделя')"
          :subtitle="filter.last_week.value.title"
          data-testid="moneymap-filter-range-week"
          @click:item="handleSelectWeek()"
          @click:btn="modalVisible.week = true"
        />
        <MoneymapFilterRangeItem
          :is_selected="range_id.startsWith('month')"
          :title="$t('Месяц')"
          :subtitle="filter.last_month.value.title"
          data-testid="moneymap-filter-range-month"
          @click:item="handleSelectMonth()"
          @click:btn="modalVisible.month = true"
        />
      </div>
    </div>
    <div class="moneymap__filter__display-graphs">
      <Text font="body-medium-14">{{ $t("Отображение графиков") }}</Text>
      <div class="moneymap__filter__list">
        <div
          v-for="item in display_graph_items"
          class="moneymap__filter__display-graphs-item"
          :key="item.id"
          :data-testid="`moneymap-filter-display-${item.id}`"
          @click="() => handleClickItem(item.id)"
        >
          <RadioButton :value="item.id" :label="item.label" :is-checked="item.id === display_graph_id" />
        </div>
      </div>
    </div>
    <template #footer>
      <div class="moneymap__filter__actions">
        <Button variant="secondary" data-testid="moneymap-filter-reset" @click="handleClickReset">
          {{ $t("Сбросить") }}
        </Button>
        <Button data-testid="moneymap-filter-apply" @click="handleClickApply">{{ $t("Применить") }}</Button>
      </div>
    </template>
  </Modal>
  <MoneymapFilterRangeTodayModal
    v-if="modalVisible.today"
    :selected_range_id="filter.state.last_selected_id_today"
    @change="handleTodayChange"
  />
  <MoneymapFilterRangeWeakModal
    v-if="modalVisible.week"
    :selected_range_id="filter.state.last_selected_id_week"
    @change="handleWeekChange"
  />
  <MoneymapFilterRangeMonthModal
    v-if="modalVisible.month"
    :selected_range_id="filter.state.last_selected_id_month"
    @change="handleMonthChange"
  />
</template>

<style scoped>
  .moneymap__filter-header {
    display: flex;
    justify-content: center;
  }

  .moneymap__filter__display-graphs,
  .moneymap__filter__range {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .moneymap__filter__list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .moneymap__filter__display-graphs-item {
    padding: 12px 0;
  }

  .moneymap__filter__actions {
    display: flex;
    gap: 8px;

    & :deep(.button) {
      flex: 1;
    }
  }
</style>
